import type { ISearchInputProps } from './SearchInput'

import { styled } from '@linaria/react'
import { ActionButtonSC } from 'atoms/ActionButton'
import { ButtonSC } from 'atoms/Button'
import { IconSC, IconSizes } from 'atoms/Icon'
import { InputActionButtonPosition, InputBackgroundColors } from 'atoms/Input'
import { COLOR, FIXED_INDENTS, MEDIA } from 'vars/index'

export const SearchWrapperSC = styled.div`
  display: flex;
  align-items: center;
  gap: var(${FIXED_INDENTS.INDENT_60});

  ${MEDIA.MIN_1240} {
    gap: var(${FIXED_INDENTS.INDENT_80});
  }

  ${MEDIA.MIN_1640} {
    gap: var(${FIXED_INDENTS.INDENT_110});
  }

  & > ${ButtonSC} {
    ${IconSC} {
      font-size: ${IconSizes.xSm};

      ${MEDIA.MIN_1240} {
        font-size: ${IconSizes.xxMedium};
      }
    }
  }
`

export const SearchInputWrapperSC = styled.div<
  Pick<ISearchInputProps, 'actionButtonPosition' | 'actionButtonIndent'> & {
    inputWrapperWidth?: string
    inputWrapperHeight?: string
  }
>`
  position: relative;
  width: ${({ inputWrapperWidth }) => inputWrapperWidth || 'auto'};
  height: ${({ inputWrapperHeight }) => inputWrapperHeight || 'auto'};
  max-width: 100%;
  position: relative;

  ${ActionButtonSC} {
    position: absolute;
    left: ${({ actionButtonPosition, actionButtonIndent }) =>
      actionButtonPosition === InputActionButtonPosition.Left
        ? actionButtonIndent
          ? `var(${actionButtonIndent})`
          : 0
        : 'auto'};
    right: ${({ actionButtonPosition, actionButtonIndent }) =>
      actionButtonPosition === InputActionButtonPosition.Right
        ? actionButtonIndent
          ? `var(${actionButtonIndent})`
          : 0
        : 'auto'};
    top: 50%;
    transform: translate(0, -50%);
    cursor: pointer;
  }
`

export const SearchInputSC = styled.input<
  ISearchInputProps & {
    inputHeight?: string
  }
>`
  width: 100%;
  height: ${({ inputHeight }) =>
    inputHeight ? `var(${inputHeight})` : 'auto'};
  font: ${({ font }) => `var(${font})`};
  color: ${({ disabled, colorDisabled, colorDefault }) =>
    disabled
      ? `var(${colorDisabled})`
      : colorDefault
        ? `var(${colorDefault})`
        : 'inherit'};
  background-color: ${({ backgroundColorDefault }) =>
    `var(${backgroundColorDefault})`};
  border: none;
  border-bottom-width: ${({ borders }) => `var(${borders})`};
  border-bottom-color: ${({
    invalid,
    borderColorDefault,
    borderColorInvalid,
  }) =>
    invalid ? `var(${borderColorInvalid})` : `var(${borderColorDefault})`};
  border-bottom-style: solid;
  padding: ${({ paddings }) => {
    if (typeof paddings === 'string') {
      return `var(${paddings})`
    }

    return paddings && paddings.length > 0
      ? paddings.map((item) => `var(${item})`)?.join(' ')
      : 0
  }};
  border-radius: ${({ corners }) => `var(${corners})`};
  cursor: pointer;
  caret-color: ${({ caretColor }) => `var(${caretColor})`};

  &::placeholder {
    color: ${({ disabled, colorDisabled, placeholderColor }) =>
      disabled
        ? `var(${colorDisabled})`
        : placeholderColor
          ? `var(${placeholderColor})`
          : 'inherit'};
  }

  &:focus {
    background-color: ${({ backgroundColorPressed }) =>
      `var(${backgroundColorPressed})`};
    border-bottom-color: ${({ borderColorPressed }) =>
      `var(${borderColorPressed})`};
  }

  &:-webkit-autofill {
    transition: background-color 5000s ease-in-out 0s;
    -webkit-text-fill-color: ${({ disabled, colorDisabled, colorDefault }) =>
      disabled
        ? `var(${colorDisabled})`
        : colorDefault
          ? `var(${colorDefault})`
          : 'inherit'};
  }

  &[type='search']::-webkit-search-decoration {
    display: ${({ isNativeDeleteButtonShown }) =>
      isNativeDeleteButtonShown ? 'block' : 'none'};
    -webkit-appearance: none;
    appearance: none;
  }

  &[type='search']::-webkit-search-cancel-button {
    display: ${({ isNativeDeleteButtonShown }) =>
      isNativeDeleteButtonShown ? 'block' : 'none'};
    -webkit-appearance: none;
    appearance: none;
    height: 16px;
    width: 16px;
    background-image: url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20clip-path%3D%22url(%23clip0_438_38728)%22%3E%0A%3Cpath%20d%3D%22M3.05017%203.05029L12.9497%2012.9498%22%20stroke%3D%22%23848486%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M3.05017%2012.9497L12.9497%203.05021%22%20stroke%3D%22%23848486%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%2F%3E%0A%3C%2Fg%3E%0A%3Cdefs%3E%0A%3CclipPath%20id%3D%22clip0_438_38728%22%3E%0A%3Crect%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22white%22%2F%3E%0A%3C%2FclipPath%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E%0A');
    color: var(${COLOR.TEXT_ICON_CONSTANT_LIGHT});
    position: relative;
    left: 20px;
    cursor: pointer;
  }
`

export const SearchInputLabelSC = styled.label<ISearchInputProps>`
  display: flex;
  width: ${({ inputLabelWidth }) => inputLabelWidth || 'auto'};
  flex-direction: column;
  padding: ${({ inputLabelPaddings }) =>
    inputLabelPaddings && inputLabelPaddings.length > 0
      ? inputLabelPaddings.map((item) => `var(${item})`)?.join(' ')
      : 0};
  margin: ${({ inputLabelMargins }) =>
    inputLabelMargins && inputLabelMargins.length > 0
      ? inputLabelMargins.map((item) => `var(${item})`)?.join(' ')
      : 0};
  position: relative;

  & > ${IconSC} {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: var(${FIXED_INDENTS.INDENT_40});

    ${MEDIA.MIN_600} {
      left: var(${FIXED_INDENTS.INDENT_60});
    }
  }

  ${MEDIA.NO_TOUCH} {
    &:hover ${SearchInputSC} {
      background-color: var(${InputBackgroundColors.SURFACE_OPACITY_LIGHT_15});

      &:focus {
        background-color: ${({ backgroundColorPressed }) =>
          `var(${backgroundColorPressed})`};
        border-bottom-color: ${({ borderColorPressed }) =>
          `var(${borderColorPressed})`};
      }
    }
  }
`
